import { useEffect, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { setSlug, setBootstrap } from './tenantSlice'
import { useGetBootstrapQuery } from './bootstrapApi'
import { applyTheme, applyFavicon } from './applyTheme'

/**
 * Path -> slug -> bootstrap -> CSS variables, in that order. Every page below
 * this provider can assume the tenant is resolved, so no screen needs its own
 * "which salon is this" logic.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const { slug = '' } = useParams<{ slug: string }>()
  const dispatch = useAppDispatch()
  const bootstrap = useAppSelector((s) => s.tenant.bootstrap)

  useEffect(() => {
    dispatch(setSlug(slug || null))
  }, [slug, dispatch])

  const { data, isLoading, isError, error } = useGetBootstrapQuery(slug, { skip: !slug })

  useEffect(() => {
    if (!data) return
    dispatch(setBootstrap(data))
    applyTheme(data.theme)
    applyFavicon(data.theme?.favicon)
    document.title = data.vendor.name
  }, [data, dispatch])

  if (isLoading || (!bootstrap && !isError)) return <TenantSplash />

  if (isError) {
    const status = (error as { status?: number | string } | undefined)?.status
    return <TenantNotFound notFound={status === 404} />
  }

  return <>{children}</>
}

function TenantSplash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-outline-variant border-t-primary"
        role="status"
        aria-label="Loading"
      />
    </div>
  )
}

function TenantNotFound({ notFound }: { notFound: boolean }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-background px-margin-mobile text-center">
      <h1 className="font-display text-headline-sm text-on-surface">
        {notFound ? 'Salon not found' : 'Cannot load this salon'}
      </h1>
      <p className="text-body-md text-on-surface-variant">
        {notFound
          ? 'Check the link you followed — it may have changed.'
          : 'Please refresh the page in a moment.'}
      </p>
    </div>
  )
}
